/**
 * 导入 PDF 模版：OSS URL → Python pdf-parse-tasks → 轮询 getLayerJson
 * UI 组件不直接发 HTTP。
 */

import { templateService } from '@/services/artApi';
import { CDP_ENDPOINTS, fetchCdpEngineJson } from '@/services/cdpEngineClient';
import { isLayerJsonAssetRef, normalizeTemplateLayerJson } from '@/utils/artMapper';

export const PDF_PARSE_POLL_INTERVAL_MS = 3000;
export const PDF_PARSE_MAX_ATTEMPTS = 100;

let pollTimerId = null;
let pollAbortController = null;

export class PdfParseCancelledError extends Error {
  constructor(message = 'PDF 解析已取消') {
    super(message);
    this.name = 'PdfParseCancelledError';
    this.cancelled = true;
  }
}

export function cancelPdfParsePolling() {
  if (pollTimerId != null) {
    clearTimeout(pollTimerId);
    pollTimerId = null;
  }
  if (pollAbortController) {
    pollAbortController.abort();
    pollAbortController = null;
  }
}

export function beginPdfParseSession() {
  cancelPdfParsePolling();
  pollAbortController = new AbortController();
  return pollAbortController.signal;
}

function isCancelled(signal, error) {
  if (signal?.aborted) return true;
  if (error?.name === 'AbortError' || error?.cancelled) return true;
  return /abort|cancel/i.test(String(error?.message || ''));
}

function throwIfCancelled(signal, error) {
  if (isCancelled(signal, error)) throw new PdfParseCancelledError();
}

function waitForPollInterval(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new PdfParseCancelledError());
      return;
    }
    const onAbort = () => {
      if (pollTimerId != null) {
        clearTimeout(pollTimerId);
        pollTimerId = null;
      }
      reject(new PdfParseCancelledError());
    };
    pollTimerId = setTimeout(() => {
      pollTimerId = null;
      signal?.removeEventListener?.('abort', onAbort);
      if (signal?.aborted) {
        reject(new PdfParseCancelledError());
        return;
      }
      resolve();
    }, ms);
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value ?? '').trim());
}

export function parseLayerJsonText(raw) {
  if (raw == null) return '';
  if (typeof raw === 'object') {
    try {
      const text = JSON.stringify(raw);
      if (!text || text === '{}' || text === '[]' || text === 'null') return '';
      return text;
    } catch {
      throw new Error('分层数据解析失败，请稍后重试');
    }
  }
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return '';
  if (isLayerJsonAssetRef(trimmed)) return trimmed;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed == null || typeof parsed !== 'object') {
      throw new Error('分层数据无效');
    }
    const serialized = JSON.stringify(parsed);
    if (!serialized || serialized === '{}' || serialized === '[]') return '';
    return trimmed;
  } catch (error) {
    if (error instanceof Error && (error.message === '分层数据无效' || error.message.startsWith('分层数据解析失败'))) {
      throw error;
    }
    throw new Error('分层数据解析失败，请稍后重试');
  }
}

export function countLayerJsonPages(layerJson) {
  try {
    const parsed = typeof layerJson === 'string' ? JSON.parse(layerJson) : layerJson;
    if (Array.isArray(parsed?.pages) && parsed.pages.length > 0) return parsed.pages.length;
  } catch {
    /* 路径或未就绪 JSON 不算页数 */
  }
  return null;
}

export function hasValidPolledLayerJson(raw) {
  try {
    return Boolean(parseLayerJsonText(raw));
  } catch {
    return false;
  }
}

export function parsePdfParseTaskResult(payload) {
  if (payload == null || typeof payload !== 'object') {
    throw new Error('PDF 解析任务无返回数据');
  }
  const data = payload.result && typeof payload.result === 'object' && !('trackId' in payload)
    ? payload.result
    : payload;
  const trackId = data?.trackId ?? data?.track_id;
  if (trackId == null || String(trackId).trim() === '') {
    throw new Error('PDF 解析任务未返回 trackId');
  }
  return {
    trackId,
    status: data?.status,
  };
}

export function parseGetLayerJsonPayload(payload) {
  if (payload == null || typeof payload !== 'object') {
    throw new Error('分层查询接口无返回数据');
  }
  if (payload.success === false || payload.error === true) {
    throw new Error(payload.errorDesc || payload.msg || payload.message || '查询分层失败');
  }
  const result = payload.result && typeof payload.result === 'object' ? payload.result : payload;
  let layerJson = '';
  try {
    layerJson = parseLayerJsonText(result?.layerJson ?? payload?.layerJson);
  } catch (error) {
    throw error;
  }
  const coverThumbUrl = [
    result?.coverThumbUrl,
    result?.coverUrl,
    result?.thumbUrl,
    result?.pageThumbUrl,
    result?.firstPageThumbUrl,
  ].map(item => String(item ?? '').trim()).find(item => /^https?:\/\//i.test(item) || /^data:image\//i.test(item)) || '';
  const originFileUrl = String(result?.originFileUrl ?? '').trim();
  const pageTotalRaw = result?.pageTotal ?? result?.pageCount ?? payload?.pageTotal;
  const pageTotalFromApi = Number(pageTotalRaw);
  const pageTotalFromJson = countLayerJsonPages(layerJson);
  const pageTotal = Number.isFinite(pageTotalFromApi) && pageTotalFromApi > 0
    ? pageTotalFromApi
    : pageTotalFromJson;
  return {
    // 线上 getLayerJson 常带 layerJson 但 success 字段缺失；有有效分层即结束轮询
    ready: Boolean(layerJson),
    layerJson,
    coverThumbUrl,
    originFileUrl: isHttpUrl(originFileUrl) ? originFileUrl : '',
    pageTotal: Number.isFinite(pageTotal) && pageTotal > 0 ? pageTotal : null,
  };
}

export async function createPdfParseTask({ ossUrl, createUserId, signal } = {}) {
  throwIfCancelled(signal);
  const url = String(ossUrl ?? '').trim();
  if (!isHttpUrl(url)) throw new Error('缺少有效的 OSS 文件地址');
  const userIdRaw = createUserId == null ? '' : String(createUserId).trim();
  if (!userIdRaw) throw new Error('缺少当前登录用户 ID');
  const numericId = Number(userIdRaw);
  const body = {
    ossUrl: url,
    createUserId: Number.isFinite(numericId) ? numericId : userIdRaw,
  };
  try {
    const payload = await fetchCdpEngineJson(CDP_ENDPOINTS.pdfParseTasks, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }, {
      timeoutMs: 60_000,
      fallbackMessage: 'PDF 解析任务创建失败',
      timeoutMessage: 'PDF 解析任务创建超时，请稍后重试',
    });
    throwIfCancelled(signal);
    return parsePdfParseTaskResult(payload);
  } catch (error) {
    throwIfCancelled(signal, error);
    throw error;
  }
}

export async function pollTemplateLayerJson(trackId, {
  signal,
  intervalMs = PDF_PARSE_POLL_INTERVAL_MS,
  maxAttempts = PDF_PARSE_MAX_ATTEMPTS,
  onAttempt,
} = {}) {
  throwIfCancelled(signal);
  if (trackId == null || String(trackId).trim() === '') {
    throw new Error('缺少 trackId，无法查询分层');
  }
  const limit = Number(maxAttempts) > 0 ? Number(maxAttempts) : PDF_PARSE_MAX_ATTEMPTS;
  const interval = Number(intervalMs) > 0 ? Number(intervalMs) : PDF_PARSE_POLL_INTERVAL_MS;

  for (let attempt = 1; attempt <= limit; attempt += 1) {
    throwIfCancelled(signal);
    onAttempt?.(attempt, limit);
    let payload;
    try {
      payload = await templateService.getLayerJson(
        { trackId },
        { silentError: true, dedupe: false, signal },
      );
    } catch (error) {
      throwIfCancelled(signal, error);
      throw error;
    }
    const parsed = parseGetLayerJsonPayload(payload);
    if (parsed.ready) {
      if (isLayerJsonAssetRef(parsed.layerJson)) {
        parsed.layerJson = await normalizeTemplateLayerJson(parsed.layerJson);
        parsed.pageTotal = parsed.pageTotal ?? countLayerJsonPages(parsed.layerJson);
      }
      return parsed;
    }
    if (attempt >= limit) break;
    const currentInterval = attempt === 1
      ? Math.min(800, interval)
      : (attempt === 2 ? Math.min(1200, interval) : (attempt === 3 ? Math.min(1800, interval) : interval));
    await waitForPollInterval(currentInterval, signal);
  }
  throw new Error('PDF 分层解析超时，请稍后重试');
}

/**
 * 创建解析任务并轮询，直到拿到有效 layerJson。
 */
export async function importPdfTemplateLayersFromOss({
  ossUrl,
  createUserId,
  signal,
  onStatus,
} = {}) {
  const sessionSignal = signal || beginPdfParseSession();
  try {
    onStatus?.('正在提交 PDF 解析任务…');
    const task = await createPdfParseTask({ ossUrl, createUserId, signal: sessionSignal });
    onStatus?.('正在解析 PDF 分层，请稍候…');
    const layers = await pollTemplateLayerJson(task.trackId, {
      signal: sessionSignal,
    });
    return {
      trackId: task.trackId,
      ...layers,
    };
  } catch (error) {
    throwIfCancelled(sessionSignal, error);
    throw error;
  } finally {
    if (!signal) cancelPdfParsePolling();
  }
}
